import type { SessionConfigFiltersType } from "@configs/custom-app-config-schema";
import { getAllItemsFromTags } from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import { isDev } from "@memorang/constants/src/urls";
import type { Distribution } from "@memorang/types/src/content";
import type { MixedItemTag, TagsByType } from "@memorang/types/src/tag";
import type { CheckboxState } from "../types/filter";
type DistributionKeys = keyof Distribution;

const initDefaultValue = 10;

export const tagIds = {
	reading: isDev ? 6479 : 1146,
	writing: isDev ? 6480 : 1147,
	listening: isDev ? 6482 : 1149,
	speaking: isDev ? 6481 : 1148,
} as const;
const getOptimalValue = (selected: number, current: number) => {
	const selectedOptiomal =
		selected > current && current > 0
			? current
			: Math.max(selected, initDefaultValue);
	const optimalValue =
		selected <= initDefaultValue * 1.5 ? selected : selectedOptiomal;
	return optimalValue;
};

const requestedDistributionKeys = (checkboxStates: CheckboxState) =>
	checkboxStates
		? (Object.keys(checkboxStates).filter(
				(k) => checkboxStates[k as DistributionKeys],
			) as DistributionKeys[])
		: [];

const calculateNewDistribution = (
	available: Distribution,
	checkboxDistKeys: DistributionKeys[],
	numItemsSelected: number,
	distributionFilterItems: SessionConfigFiltersType,
	studyRecommenedTopics?: boolean,
) => {
	const showItemsSlider =
		useAppStore.getState().app.sessionConfig?.showItemsSlider ?? false;

	const newDist = Object.assign(
		{},
		distributionFilterItems.reduce((p, i) => {
			p[i.distKey] = 0;
			return p;
		}, {} as Distribution),
		{ none: 0 },
	);

	const distributionKeys = Object.keys(newDist);

	let remaining = numItemsSelected;
	const usedKeys = new Set();
	const updateDist = (key: DistributionKeys) => {
		if (remaining > 0) {
			const numCurrentTotalRequiredItems = Object.keys(newDist).reduce<number>(
				(p, i) => p + newDist[i],
				0,
			);
			const avail =
				studyRecommenedTopics && key === "none" ? 0 : available[key];
			if (numCurrentTotalRequiredItems < 50 || !showItemsSlider) {
				newDist[key] += Math.min(remaining, avail);
				remaining = Math.max(0, remaining - avail);
				usedKeys.add(key);
			}
		}
	};
	if (checkboxDistKeys.length) {
		checkboxDistKeys.forEach(updateDist);
	} else {
		distributionKeys.forEach(updateDist);
	}
	// if (remaining > 0) {
	//   distributionKeys.forEach((k) => {
	//     if (!usedKeys.has(k)) {
	//       updateDist(k as DistributionKeys);
	//     }
	//   });
	// }

	return newDist;
};

export const calculateQuestionCount = (
	checkBoxStatus: CheckboxState,
	available: Distribution,
) => {
	const distributionKeys = Object.keys(available);
	return distributionKeys.reduce((p, i) => {
		let result = p;
		if (!checkBoxStatus) {
			return result;
		}

		if (checkBoxStatus[i as keyof Distribution]) {
			result += available[i as keyof Distribution];
		}

		return result;
	}, 0);
};
// From QuestionCount -> CheckBox
const calculateCheckBoxStatus = (required: Distribution): CheckboxState => {
	const distributionKeys = Object.keys(required);
	const checkBoxState: CheckboxState = {};
	const statusForKey = (key: DistributionKeys) => {
		if (required[key] > 0) {
			return true;
		}
		return false;
	};

	for (const t of distributionKeys) {
		checkBoxState[t] = statusForKey(t as DistributionKeys);
	}
	return checkBoxState;
};

const onCheckBoxStatusUpdated = (
	available: Distribution,
	checkBoxStates: CheckboxState,
	numSelectedItems: number,
	distributionFilterItems: SessionConfigFiltersType,
) => {
	const questionCount = calculateQuestionCount(checkBoxStates, available);
	const selectedCheckboxesKeys = requestedDistributionKeys(checkBoxStates);

	const requiredDistribution = calculateNewDistribution(
		available,
		selectedCheckboxesKeys as DistributionKeys[],
		numSelectedItems,
		distributionFilterItems,
	);

	return {
		numSelectedItems,
		requiredDistribution,
		questionCount,
	};
};

const getNewDistributionCheckboxstatesAndCount = (
	available: Distribution,
	checkBoxStates: CheckboxState,
	numAvailableItems: number,
	distributionFilterItems: SessionConfigFiltersType,
	optimalDefault?: number,
	studyRecommenedTopics?: boolean,
) => {
	const selectedCheckboxesKeys = requestedDistributionKeys(checkBoxStates);
	let optimalDist: Distribution | undefined;
	if (optimalDefault) {
		optimalDist = calculateNewDistribution(
			available,
			selectedCheckboxesKeys,
			optimalDefault,
			distributionFilterItems,
		);
	}
	const forCheckboxDist = calculateNewDistribution(
		available,
		selectedCheckboxesKeys,
		numAvailableItems,
		distributionFilterItems,
		studyRecommenedTopics,
	);
	const newStatus = calculateCheckBoxStatus(forCheckboxDist);
	return {
		distributionCheckboxStates: newStatus,
		requiredDistribution: optimalDist || forCheckboxDist,
	};
};

const initializeCheckboxStates = (
	availableItems: number,
	availableDist: Distribution,
	showItemsSlider: boolean,
	studyRecommenedTopics?: boolean,
): CheckboxState => {
	const distributionKeys = Object.keys(availableDist) as DistributionKeys[];
	const masteryLevels = useAppStore.getState().app.masteryLevels!;

	const checkboxStates: CheckboxState = {
		...Object.assign(
			{},
			...masteryLevels.map((item) => ({
				[item.id]: false,
			})),
		),
		none: false,
	};

	let remainingItems = showItemsSlider
		? Math.min(availableItems, 50)
		: availableItems;

	const priorityOrder: DistributionKeys[] = ["low", "medium", "none", "high"];
	const rank = new Map(priorityOrder.map((k, i) => [k, i]));

	const orderedKeys = studyRecommenedTopics
		? [...distributionKeys].sort(
				(a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999),
			)
		: distributionKeys;

	for (const bucket of orderedKeys) {
		if (remainingItems > 0 && availableDist[bucket] > 0 && checkboxStates) {
			checkboxStates[bucket] = true;
			remainingItems -= availableDist[bucket];
		}
	}

	return checkboxStates;
};

const calculateCheckboxStatusAndAvailableItems = (
	updatedDistribution: Distribution,
	showItemsSlider: boolean,
	studyRecommenedTopics?: boolean,
) => {
	let availableItems = 0;

	for (const key of Object.keys(updatedDistribution)) {
		const value = updatedDistribution[key as keyof Distribution];
		if (value > 0) {
			availableItems += value;
		}
	}

	const checkboxStatus = initializeCheckboxStates(
		availableItems,
		updatedDistribution,
		showItemsSlider,
		studyRecommenedTopics,
	);

	return { checkboxStatus, availableItems };
};

const getTagIds = (
	status: CheckboxState,
	freeTagIds?: number[],
	hasAccess?: boolean,
) => {
	const checkedTagIds = status
		? Object.keys(status)
				.filter((key) => status[key])
				.map(Number)
		: [];
	const uncheckedTagIds =
		hasAccess && status
			? Object.keys(status)
					.filter((key) => !status[key])
					.map(Number)
			: freeTagIds;
	return { checkedTagIds, uncheckedTagIds };
};

const getFinalTagIds = (checkedTagIds: number[], uncheckedTagIds: number[]) => {
	return checkedTagIds.length > 0 ? checkedTagIds : uncheckedTagIds;
};

const filterTagsByTagIds = (tags: MixedItemTag[], tagIds: number[]) => {
	return tags.filter(({ id }) => tagIds?.includes(id));
};

const getFinalTagsDistForMode = (
	tags: MixedItemTag[],
	mode: string,
	showLearningMode?: boolean,
) => {
	if (showLearningMode) {
		const allItems = getAllItemsFromTags(tags);
		const currentTagsDistForMode = allItems.filter(
			(item) => item?.type === mode,
		);
		return currentTagsDistForMode;
	}
	return tags;
};
const groupSelectedTagsByType = (
	selectedTagIds: number[],
	tagsByType: TagsByType,
) => {
	const groupedByType: Record<string, number[]> = {};

	for (const tagId of selectedTagIds) {
		for (const [type, typeTags] of Object.entries(tagsByType)) {
			const foundTag = typeTags.find((tag) => tag.id === tagId);
			if (foundTag) {
				if (!groupedByType[type]) {
					groupedByType[type] = [];
				}
				groupedByType[type].push(tagId);
				break;
			}
		}
	}

	return Object.values(groupedByType);
};
const calculateProficiencyBasedDistribution = (
	tags: MixedItemTag[],
	distributionFilterItems: SessionConfigFiltersType,
) => {
	const result = tags.map((tag) => {
		return {
			...tag,
			distribution: tag.items!.reduce(
				(acc, item) => {
					const proficiencyLevel = getProficiencyLevel(
						tag.label!,
						item.stats?.score,
					);
					acc[proficiencyLevel] = (acc[proficiencyLevel] || 0) + 1;
					return acc;
				},
				{
					...distributionFilterItems.reduce(
						(acc, item) => {
							acc[item.distKey] = 0;
							return acc;
						},
						{} as Record<string, number>,
					),
				} as Record<string, number>,
			),
		};
	});

	const accumulatedDistribution = result.reduce(
		(acc, item) => {
			for (const key of Object.keys(item.distribution)) {
				acc[key] = (acc[key] || 0) + item.distribution[key];
			}
			return acc;
		},
		{} as Record<string, number>,
	);

	return {
		mappedTags: result,
		accumulatedDistribution,
	};
};
const getProficiencyLevel = (
	skill: string,
	score?: number,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
): "advanced" | "intermediate" | "basic" | "none" => {
	if (score == null) {
		return "none";
	}

	switch (skill) {
		case "Reading": {
			if (score >= 80) {
				return "advanced";
			}
			if (score >= 13) {
				return "intermediate";
			}
			return "basic";
		}

		case "Listening": {
			if (score >= 73) {
				return "advanced";
			}
			if (score >= 30) {
				return "intermediate";
			}
			return "basic";
		}

		case "Speaking": {
			if (score >= 83) {
				return "advanced";
			}
			if (score >= 33) {
				return "intermediate";
			}
			return "basic";
		}

		case "Writing": {
			if (score >= 80) {
				return "advanced";
			}
			if (score >= 43) {
				return "intermediate";
			}
			return "basic";
		}

		default:
			throw new Error(`Unknown skill: ${skill}`);
	}
};

export {
	getOptimalValue,
	requestedDistributionKeys,
	calculateNewDistribution,
	onCheckBoxStatusUpdated,
	calculateCheckBoxStatus,
	getNewDistributionCheckboxstatesAndCount,
	initializeCheckboxStates,
	calculateCheckboxStatusAndAvailableItems,
	getTagIds,
	getFinalTagIds,
	filterTagsByTagIds,
	getFinalTagsDistForMode,
	getProficiencyLevel,
	groupSelectedTagsByType,
	calculateProficiencyBasedDistribution,
};
